import { ChangeDetectionStrategy, Component, OnInit, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogActions, MatDialogClose, MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { ReactiveFormsModule, FormControl, FormGroup, ValidatorFn, ValidationErrors, AbstractControl, Validators } from '@angular/forms';
import { FileUploadModule, FileUploadValidators } from '@iplab/ngx-file-upload';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { AnexoService } from '../../services/anexo.service';
import { Anexo } from '../../models/Anexo';

import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

export function fileRequiredValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const files: File[] = control.value;
    return files && files.length > 0 ? null : { required: true };
  };
}

export function fileNameLengthValidator(maxLength: number = 128): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const files: File[] = control.value;

    if (files && files.length > 0) {
      const invalidFile = files.find(file => file.name.length > maxLength);
      return invalidFile
        ? { fileNameTooLong: { fileName: invalidFile.name, maxLength, length: invalidFile.name.length } }
        : null;
    }

    return null;
  };
}

@Component({
    selector: 'app-dlg-anexos',
    imports: [
    MatButtonModule,
    MatDialogActions,
    MatDialogClose,
    MatDialogTitle,
    MatDialogContent,
    ReactiveFormsModule,
    FileUploadModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatProgressSpinnerModule
],
    changeDetection: ChangeDetectionStrategy.Default,
    templateUrl: './dlg-anexos.component.html',
    styleUrl: './dlg-anexos.component.scss'
})
export class DlgAnexosComponent implements OnInit {
  readonly dialogRef = inject(MatDialogRef<DlgAnexosComponent>)

  filesLimitMessageError = ""
  numberOfFilesLimit = 4
  filesSizeLimit = 41943040
  usedKB = 0

  postingAnexos = false

  private filesControl = new FormControl<File[]>([], [
    FileUploadValidators.filesLimit(this.numberOfFilesLimit),
    FileUploadValidators.sizeLimit(this.filesSizeLimit),
    fileNameLengthValidator(),
    fileRequiredValidator(),
  ]);

  private descriptionControl = new FormControl("", {
    validators: [
      Validators.maxLength(500)
    ]
  })

  public addAnexoForm = new FormGroup({
    files: this.filesControl,
    description: this.descriptionControl,
  });

  constructor(
    private anexoService: AnexoService,
  ) {
    this.filesLimitMessageError = `M\xe1ximo ${this.numberOfFilesLimit} ${this.numberOfFilesLimit > 1 ? 'archivos' : 'archivo'} permitido${this.numberOfFilesLimit > 1 ? 's' : ''}`
  }

  ngOnInit(): void {
    this.addAnexoForm.get('files')?.valueChanges.subscribe(files => {
      if (files) {
        this.updateUsedKB(files);
      }
    });
  }

  updateUsedKB(files: File[]) {
    this.usedKB = files.reduce((total, file) => total + file.size / 1024 / 1024, 0); // Convertir bytes a KB
    this.updateKBsUsedDisplay();
  }

  updateKBsUsedDisplay() {
    const KB = this.filesSizeLimit / 1024 / 1024;
    return `${this.usedKB.toFixed(2)}/${KB.toFixed(0)} MB`;
  }

  manageUploadAnexo() {
    this.postingAnexos = true
    const files = this.addAnexoForm.value.files
    const desc = this.addAnexoForm.value.description
    let anexo: Anexo
    if (desc) {
      anexo = {
        file: files!,
        desc: desc,
      }
    } else {
      anexo = {
        file: files!
      }
    }

    this.anexoService.postAnexosTemporales(anexo).subscribe({
      next: (response) => {
        this.dialogRef.close(response)
      },
      error: (error) => {
        this.postingAnexos = false
      }
    })

  }
}
